// Het vlak Gebruikers & engagement (W9; sinds W6 ronde 7 in twee tabbladen).
//
//  · Mensen  — wie er binnen mag: de W9-lijst met uitnodigen en wijzigen.
//              Verhuisd uit BeheerPagina.tsx toen Beheer acht vlakken kreeg
//              (W6, besluit V1); het gedrag is ongewijzigd.
//  · Gebruik — wie het platform hoe vaak gebruikt en met welk krediet
//              (research §3.8, GebruikersGebruik.tsx). Alleen tellingen en
//              tijden (AVG, V2); daar staat ook het persoonlijke maximum.
//
// Gebruik haalt pas op als iemand het tabblad opent — wie alleen komt
// uitnodigen, vraagt de tellingen niet — en blijft daarna getekend, verborgen:
// wie tijdens een opslag even naar Mensen kijkt, komt terug bij hetzelfde
// (zelfde afspraak als Configuratie, review ronde 6).
//
// Wat Mensen bewust NIET doet: de regels voorspellen. Welke combinatie van
// rol, propositie en adminvlag mag, staat in `_valideer_toewijzing`
// (sof/readapi/beheer.py) — één plek, zodat uitnodigen en wijzigen niet uit
// elkaar lopen. Het formulier stuurt op en toont de Nederlandse reden die
// terugkomt. Zou het scherm dezelfde regels nabouwen, dan zijn het er twee.
//
// De keuzelijsten (rollen, proposities) komen mee met de gebruikerslijst, zodat
// dit scherm geen enum kent die in `config/` leeft.
//
// Pilot 26-10 (baan 1D): uitnodigen laat Supabase echt een mail sturen. Het
// antwoord zegt of dat lukte (`uitnodiging`); lukte het niet, dan staat de
// reden hier letterlijk en kan de beheerder "Opnieuw sturen". Een vinkje
// "Testaccount" markeert de testaccounts van de owner (gebruikers.is_test).

import { useEffect, useState } from "react";

import { Card, SectionLabel, TableHead } from "../../components/ui";
import { EmptyReason, LoadError } from "../../components/Empty";
import { ApiError, api } from "../../lib/api";
import { meldMijGewijzigd } from "../../lib/mij";
import type { Resource } from "../../lib/useResource";
import type { Gebruiker, GebruikersResponse, Uitnodiging } from "../../contracts";
import { Dialoog, Foutmelding, Invoer, KeuzeKaarten, Knop, PlatformStijl, Schakelaar, Veldblok } from "../../stijl";
import { color, radius, size, weight } from "../../tokens";
import { Gebruik } from "./GebruikersGebruik";
import { StatusPil } from "./StatusPil";
import { geenRolReden } from "./woorden";

/** De ascii-sleutel reist mee naar het gebruikslog ("Beheer/Gebruikers/Gebruik");
 *  het label is wat de beheerder leest. */
type TabKey = "Mensen" | "Gebruik";

const TABS: readonly { key: TabKey; label: string }[] = [
  { key: "Mensen", label: "Mensen" },
  { key: "Gebruik", label: "Gebruik" },
];

const KOLOMMEN = "1.6fr 1.1fr 1fr 0.7fr 0.8fr 150px";

/** Wat de beheerder na een uitnodiging of "opnieuw sturen" leest. */
export type Melding = { tekst: string; goed: boolean };

/** De uitkomst van de mail in één zin voor de beheerder. */
export function mailMelding(email: string, u: Uitnodiging | undefined): Melding {
  if (!u) return { tekst: `${email} staat in de lijst.`, goed: true };
  if (u.verstuurd) {
    const watNu =
      " Wat nu: open die mail (binnen een paar minuten; kijk ook in spam) en klik op de link." +
      ' Niets binnen na 10 minuten? Klik bij deze persoon op "Opnieuw sturen".';
    return {
      tekst:
        (u.via === "magiclink"
          ? `Mail verstuurd naar ${email}: dit adres had al een account en kreeg een inloglink.`
          : `Mail verstuurd naar ${email}: een uitnodiging met een inloglink.`) + watNu,
      goed: true,
    };
  }
  return { tekst: u.reden ?? `Er is géén mail naar ${email} verstuurd.`, goed: false };
}

export function Gebruikers({
  gebruikers,
  mijnEmail,
}: {
  /** Opgehaald door de schil, die het aantal ook in de paginakop toont. */
  gebruikers: Resource<GebruikersResponse>;
  mijnEmail?: string;
}) {
  const [tab, setTab] = useState<TabKey>("Mensen");
  // Gebruik staat er vanaf de eerste keer dat iemand het opent (zie de kop).
  const [gebruikGeopend, setGebruikGeopend] = useState(false);

  // Welk tabblad, zoals de schil het vlak meldt (W6, research §4): alleen WAAR
  // iemand kijkt, nooit wat erin staat. Ook het eerste tabblad telt.
  // Vuur-en-vergeet: een telling die faalt mag het scherm nooit raken.
  useEffect(() => {
    void api.gebruik({ scherm: `Beheer/Gebruikers/${tab}` }).catch(() => {});
  }, [tab]);

  const kies = (nieuw: TabKey) => {
    setTab(nieuw);
    if (nieuw === "Gebruik") setGebruikGeopend(true);
  };

  return (
    <div>
      {/* Hetzelfde gesegmenteerde patroon als de subnavigatie van Beheer. */}
      <nav
        aria-label="Onderdelen van Gebruikers & engagement"
        style={{
          display: "inline-flex",
          flexWrap: "wrap",
          gap: 4,
          background: color.lineFaint,
          borderRadius: radius.input,
          padding: 3,
          marginBottom: 18,
        }}
      >
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={"view-btn" + (tab === t.key ? " active" : "")}
            aria-pressed={tab === t.key}
            onClick={() => kies(t.key)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {/* `hidden` haalt een verborgen tabblad ook uit de toetsenbordvolgorde
          en uit wat een schermlezer voorleest. */}
      <div data-paneel="Mensen" hidden={tab !== "Mensen"}>
        <Mensen gebruikers={gebruikers} mijnEmail={mijnEmail} />
      </div>
      {gebruikGeopend && (
        <div data-paneel="Gebruik" hidden={tab !== "Gebruik"}>
          {/* De lijst van Mensen als versie: verandert hij (een uitnodiging,
              een andere rol), dan haalt Gebruik opnieuw op. */}
          <Gebruik lijstVersie={gebruikers.data} />
        </div>
      )}
    </div>
  );
}

/** Het tabblad Mensen: de W9-lijst met uitnodigen en wijzigen, ongewijzigd. */
function Mensen({
  gebruikers,
  mijnEmail,
}: {
  /** Opgehaald door de schil, die het aantal ook in de paginakop toont. */
  gebruikers: Resource<GebruikersResponse>;
  mijnEmail?: string;
}) {
  const [bewerken, setBewerken] = useState<Gebruiker | null>(null);
  const [uitnodigen, setUitnodigen] = useState(false);
  const [melding, setMelding] = useState<Melding | null>(null);
  const [stuurtNaar, setStuurtNaar] = useState<string | null>(null);

  const lijst = gebruikers.data?.gebruikers ?? [];

  async function opnieuwSturen(g: Gebruiker) {
    setStuurtNaar(g.id);
    setMelding(null);
    try {
      const antwoord = await api.opnieuwSturen(g.id);
      setMelding(mailMelding(g.email, antwoord.uitnodiging));
    } catch (err) {
      setMelding({
        tekst: err instanceof ApiError ? err.message : "Opnieuw sturen mislukt.",
        goed: false,
      });
    } finally {
      setStuurtNaar(null);
    }
  }

  return (
    <PlatformStijl>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 10,
        }}
      >
        <div style={{ marginBottom: -10 }}>
          <SectionLabel>Gebruikers</SectionLabel>
        </div>
        <Knop variant="primair" onClick={() => setUitnodigen(true)}>
          Iemand uitnodigen
        </Knop>
      </div>

      {melding && (
        <p
          role="status"
          style={{
            fontSize: size.meta,
            color: melding.goed ? color.body : color.accentRed,
            margin: "0 0 10px",
          }}
        >
          {melding.tekst}
        </p>
      )}

      <Card>
        <TableHead
          cols={KOLOMMEN}
          labels={["Naam", "Rol", "Propositie", "Beheer", "Status", ""]}
        />
        {gebruikers.error ? (
          <LoadError reason={gebruikers.error} onRetry={gebruikers.reload} />
        ) : gebruikers.loading ? (
          <div style={{ padding: 18 }}>
            <EmptyReason reason="de gebruikerslijst wordt opgehaald" />
          </div>
        ) : (
          lijst.map((g) => (
            <GebruikerRij
              key={g.id}
              gebruiker={g}
              ikZelf={Boolean(mijnEmail && g.email === mijnEmail)}
              onBewerk={() => setBewerken(g)}
              onOpnieuw={() => void opnieuwSturen(g)}
              stuurt={stuurtNaar === g.id}
            />
          ))
        )}
      </Card>

      <GebruikerFormulier
        open={uitnodigen}
        keuzes={gebruikers.data}
        onSluit={() => setUitnodigen(false)}
        onKlaar={(m) => {
          setUitnodigen(false);
          setMelding(m ?? null);
          gebruikers.reload();
        }}
      />
      <GebruikerFormulier
        open={bewerken !== null}
        bestaand={bewerken}
        keuzes={gebruikers.data}
        onSluit={() => setBewerken(null)}
        onKlaar={() => {
          const eigenRij = Boolean(mijnEmail && bewerken?.email === mijnEmail);
          setBewerken(null);
          gebruikers.reload();
          // Wijzig je je eigen rol of propositie, dan klopt /api/mij niet meer —
          // en daar hangt de hele personalisatie aan.
          if (eigenRij) meldMijGewijzigd();
        }}
      />
    </PlatformStijl>
  );
}

/** Bij een testaccount: hoe je als die persoon inlogt. Alleen uitleg; Beheer stuurt
 *  zelf geen mail en geeft geen extra rechten. */
export function InlogRegel({ email }: { email: string }) {
  return (
    <p
      data-testid="inlog-regel"
      style={{ gridColumn: "1 / -1", margin: "8px 0 0", color: color.soft, fontSize: size.meta, lineHeight: 1.45 }}
    >
      Zo log je in als deze persoon: ga naar het inlogscherm, vul <strong>{email}</strong> in en
      kies “Stuur mij een inloglink”. De link komt in de mailbox van dit adres.
    </p>
  );
}

function GebruikerRij({
  gebruiker,
  ikZelf,
  onBewerk,
  onOpnieuw,
  stuurt,
}: {
  gebruiker: Gebruiker;
  ikZelf: boolean;
  onBewerk: () => void;
  onOpnieuw: () => void;
  stuurt: boolean;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: KOLOMMEN,
        alignItems: "center",
        padding: "12px 16px",
        borderTop: `1px solid ${color.lineFaint}`,
        fontSize: size.meta,
        color: color.body,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: weight.medium, color: color.ink }}>
          {gebruiker.naam || gebruiker.email}
          {ikZelf && (
            <span style={{ color: color.faint, fontWeight: weight.regular }}> · jij</span>
          )}
          {gebruiker.isTest && (
            <span
              title="Testaccount: keuzes krijgen het label test"
              style={{
                marginLeft: 6,
                padding: "1px 6px",
                borderRadius: radius.input,
                border: `1px solid ${color.border}`,
                color: color.faint,
                fontSize: size.label,
                fontWeight: weight.regular,
              }}
            >
              test
            </span>
          )}
        </div>
        <div style={{ color: color.faint, overflow: "hidden", textOverflow: "ellipsis" }}>
          {gebruiker.email}
        </div>
      </div>

      <div>
        {gebruiker.rolLabel ?? (
          // Geen rol is geldig zolang je beheerder bent (W9-besluit B3): admin
          // is een vlag, geen rol. Daarom staat hier een reden en geen streepje.
          <EmptyReason reason={geenRolReden(gebruiker.isAdmin)} />
        )}
      </div>

      <div>
        {gebruiker.propositieOnbekend ? (
          <span style={{ color: color.accentAmber }}>
            {gebruiker.propositie} — niet meer in de lijst
          </span>
        ) : (
          (gebruiker.propositieLabel ?? <EmptyReason reason="alle proposities" />)
        )}
      </div>

      <div>{gebruiker.isAdmin ? "beheerder" : <EmptyReason reason="nee" />}</div>

      <div>
        <StatusPil status={gebruiker.status} />
      </div>

      <div style={{ textAlign: "right", display: "flex", gap: 6, justifyContent: "flex-end" }}>
        {gebruiker.status === "uitgenodigd" && (
          <Knop onClick={onOpnieuw} disabled={stuurt} title="Stuur de uitnodigingsmail nog een keer">
            {stuurt ? "Bezig…" : "Opnieuw sturen"}
          </Knop>
        )}
        <Knop variant="zacht" onClick={onBewerk}>
          Wijzig
        </Knop>
      </div>

      {gebruiker.isTest && <InlogRegel email={gebruiker.email} />}
    </div>
  );
}

// ── Het formulier: uitnodigen én wijzigen, want de regels zijn dezelfde ──────

function GebruikerFormulier({
  open,
  bestaand,
  keuzes,
  onSluit,
  onKlaar,
}: {
  open: boolean;
  bestaand?: Gebruiker | null;
  keuzes?: GebruikersResponse | null;
  onSluit: () => void;
  onKlaar: (melding?: Melding) => void;
}) {
  const wijzigen = Boolean(bestaand);
  const [email, setEmail] = useState("");
  const [naam, setNaam] = useState("");
  const [rol, setRol] = useState("");
  const [propositie, setPropositie] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [isTest, setIsTest] = useState(false);
  const [status, setStatus] = useState<Gebruiker["status"]>("uitgenodigd");
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  // De velden vullen zich bij het openen, niet bij elke render.
  const [geopendVoor, setGeopendVoor] = useState<string | null>(null);

  const sleutel = open ? (bestaand?.id ?? "nieuw") : null;
  if (sleutel !== geopendVoor) {
    setGeopendVoor(sleutel);
    setEmail(bestaand?.email ?? "");
    setNaam(bestaand?.naam ?? "");
    setRol(bestaand?.rol ?? "");
    setPropositie(bestaand?.propositie ?? "");
    setIsAdmin(bestaand?.isAdmin ?? false);
    setIsTest(bestaand?.isTest ?? false);
    setStatus(bestaand?.status ?? "uitgenodigd");
    setFout(null);
  }

  if (!open) return null;

  const rollen = keuzes?.rollen ?? [];
  // In de pilot alleen de vier pilotproposities. Een bestaande rij met een
  // andere propositie (van vóór de pilot) houdt die als eigen keuze, zodat de
  // lijst hem niet stil op "Geen propositie" zet.
  const pilot = keuzes?.proposities ?? [];
  const eigen =
    bestaand?.propositie && !pilot.some((p) => p.key === bestaand.propositie)
      ? [{ key: bestaand.propositie, naam: bestaand.propositieLabel ?? bestaand.propositie }]
      : [];
  const proposities = [...pilot, ...eigen];
  const gekozenRol = rollen.find((r) => r.key === rol);

  async function opslaan() {
    setBezig(true);
    setFout(null);
    try {
      const velden = {
        naam: naam.trim() || null,
        // De keuzelijst komt van de server (`rollen` in dezelfde response), dus
        // de waarde is per constructie een geldige sleutel. Het contract kent de
        // enum; het formulier draagt hem als tekst. Weigert de server hem toch,
        // dan is dat een 400 met reden — geen stille mismatch.
        rol: (rol || null) as Gebruiker["rol"],
        propositie: propositie ? propositie : null,
        isAdmin,
      };
      if (bestaand) {
        await api.wijzigGebruiker(bestaand.id, {
          ...velden,
          status,
          // Alleen meesturen als het verandert: zo kan wijzigen ook vóór
          // migratie 0053 (de kolom is_test).
          ...(isTest !== (bestaand.isTest ?? false) ? { isTest } : {}),
        });
        onKlaar();
      } else {
        const adres = email.trim().toLowerCase();
        const nieuw = await api.uitnodigen({ email: adres, ...velden, ...(isTest ? { isTest } : {}) });
        onKlaar(mailMelding(adres, nieuw.uitnodiging));
      }
    } catch (err) {
      // De service schrijft de reden ("Directie hoort bij een propositie — kies
      // er een."). Die tonen we letterlijk; hem hier nabouwen zou betekenen dat
      // de regel op twee plekken leeft.
      setFout(err instanceof ApiError ? err.message : "Opslaan mislukt.");
    } finally {
      setBezig(false);
    }
  }

  return (
    <Dialoog
      open={open}
      titel={wijzigen ? "Gebruiker wijzigen" : "Iemand uitnodigen"}
      sub={
        wijzigen
          ? bestaand?.email
          : "Er gaat een mail met een inloglink naar dit adres. Lukt dat niet, dan zegt dit scherm waarom."
      }
      onSluit={onSluit}
    >
      <div className="s-formulier">
        {!wijzigen && (
          <Veldblok label="E-mailadres">
            <Invoer
              type="email"
              value={email}
              autoFocus
              onChange={(e) => setEmail(e.target.value)}
              placeholder="naam@summacollege.nl"
            />
          </Veldblok>
        )}

        <Veldblok label="Naam">
          <Invoer type="text" value={naam} onChange={(e) => setNaam(e.target.value)} placeholder="Voor- en achternaam" />
        </Veldblok>

        <KeuzeKaarten
          label="Rol"
          naam="gebruiker-rol"
          compact
          waarde={rol}
          onKies={setRol}
          opties={[{ waarde: "", titel: "Geen rol — beheert alleen" }, ...rollen.map((r) => ({ waarde: r.key, titel: r.label }))]}
          uitleg="Een rol is wat iemand ziet. Leeg mag, zolang deze persoon beheerder is."
        />

        <KeuzeKaarten
          label="Propositie"
          naam="gebruiker-propositie"
          compact
          waarde={propositie}
          onKies={setPropositie}
          opties={[{ waarde: "", titel: "Geen propositie" }, ...proposities.map((p) => ({ waarde: p.key, titel: p.naam }))]}
          uitleg={
            gekozenRol?.propositieVerplicht
              ? `${gekozenRol.label} hoort bij één school — kies er een.`
              : "Leeg = geen sectorfilter; cross-sector of beheerder."
          }
        />

        <Schakelaar
          aan={isAdmin}
          onWissel={setIsAdmin}
          titel="Beheerder"
          uitleg="Mag uitnodigen en de wachtrij beslissen. Staat naast de rol, niet in plaats ervan — en overstijgt de propositie."
        />

        <Schakelaar
          aan={isTest}
          onWissel={setIsTest}
          titel="Testaccount"
          uitleg="Voor het testen van een rol. Keuzes van dit account krijgen het label test en kunnen later gewist worden, zodat een echte gebruiker een schoon plan krijgt."
        />

        {wijzigen && (
          <KeuzeKaarten
            label="Status"
            naam="gebruiker-status"
            compact
            waarde={status}
            onKies={setStatus}
            opties={[
              { waarde: "uitgenodigd", titel: "Uitgenodigd" },
              { waarde: "actief", titel: "Actief" },
              { waarde: "inactief", titel: "Inactief" },
            ]}
            uitleg="Inactief trekt de toegang in; een inlog maakt dat nooit ongedaan."
          />
        )}

        {fout && <Foutmelding>{fout}</Foutmelding>}

        <div className="s-formulier__voet">
          <Knop onClick={onSluit}>Annuleren</Knop>
          <Knop variant="primair" onClick={() => void opslaan()} disabled={bezig}>
            {bezig ? "Bezig…" : wijzigen ? "Opslaan" : "Uitnodigen"}
          </Knop>
        </div>
      </div>
    </Dialoog>
  );
}
